// SVG geometry helpers for the score dial — build the annular wedge paths
// shared by the web (<path>) and PDF (@react-pdf <Path>) dial renderers.

import {
  CENTER_X,
  CENTER_Y,
  CORNER_R,
  INNER_R,
  OUTER_R,
  SEGMENT_GAP_DEG,
  SEGMENT_SPAN,
  TOTAL_SEGMENTS,
} from './constants';

// Converts a polar angle + radius into an [x, y] point in SVG space.
// 0deg = leftmost point of the arc, 90deg = top, 180deg = rightmost.
const polarToPoint = (angleDeg: number, radius: number): [number, number] => {
  const radians = (angleDeg * Math.PI) / 180;

  return [
    CENTER_X - radius * Math.cos(radians),
    CENTER_Y - radius * Math.sin(radians),
  ];
};

const formatPoint = ([x, y]: [number, number]): string => `${x} ${y}`;

const arcTrimDeg = (radius: number): number =>
  (CORNER_R / radius) * (180 / Math.PI);

// Builds the SVG path `d` for a single wedge segment by index (0-9).
export const buildWedgePath = (index: number): string => {
  const startDeg = index * SEGMENT_SPAN + SEGMENT_GAP_DEG / 2;
  const endDeg = (index + 1) * SEGMENT_SPAN - SEGMENT_GAP_DEG / 2;

  // Arc endpoints, pulled in by the fillet trim so the rounded corners align.
  const innerStart = polarToPoint(startDeg + arcTrimDeg(INNER_R), INNER_R);
  const innerEnd = polarToPoint(endDeg - arcTrimDeg(INNER_R), INNER_R);
  const outerStart = polarToPoint(startDeg + arcTrimDeg(OUTER_R), OUTER_R);
  const outerEnd = polarToPoint(endDeg - arcTrimDeg(OUTER_R), OUTER_R);

  // Exact corners — used as the control points for the quadratic fillets.
  const innerStartCorner = polarToPoint(startDeg, INNER_R);
  const outerStartCorner = polarToPoint(startDeg, OUTER_R);
  const outerEndCorner = polarToPoint(endDeg, OUTER_R);
  const innerEndCorner = polarToPoint(endDeg, INNER_R);

  // Points on the straight radial edges where each fillet begins/ends.
  const innerStartRadial = polarToPoint(startDeg, INNER_R + CORNER_R);
  const outerStartRadial = polarToPoint(startDeg, OUTER_R - CORNER_R);
  const outerEndRadial = polarToPoint(endDeg, OUTER_R - CORNER_R);
  const innerEndRadial = polarToPoint(endDeg, INNER_R + CORNER_R);

  return [
    `M ${formatPoint(innerStart)}`,
    `Q ${formatPoint(innerStartCorner)} ${formatPoint(innerStartRadial)}`,
    `L ${formatPoint(outerStartRadial)}`,
    `Q ${formatPoint(outerStartCorner)} ${formatPoint(outerStart)}`,
    `A ${OUTER_R} ${OUTER_R} 0 0 1 ${formatPoint(outerEnd)}`,
    `Q ${formatPoint(outerEndCorner)} ${formatPoint(outerEndRadial)}`,
    `L ${formatPoint(innerEndRadial)}`,
    `Q ${formatPoint(innerEndCorner)} ${formatPoint(innerEnd)}`,
    `A ${INNER_R} ${INNER_R} 0 0 0 ${formatPoint(innerStart)}`,
    'Z',
  ].join(' ');
};

// How many of the TOTAL_SEGMENTS wedges to fill for a 0-100 score (clamped).
export const filledSegmentCount = (score: number): number =>
  Math.round((Math.max(0, Math.min(100, score)) / 100) * TOTAL_SEGMENTS);
